import { Link as InertiaLink } from '@inertiajs/react'

import { Link } from '@atoms'
import { SimpleStatCard, SplitStatCard, BreakdownLine } from '@molecules/StatCard'
import { scopedTranslation } from '@utils/I18n'
import type { GroupsIndexQueryType } from '@frontend/graphql/queries/groups'

import GroupAddress from './GroupAddress'
import GroupCriteria from './GroupCriteria'
import PointPeople from './PointPeople'
import LeaderNames from './LeaderNames'
import { GroupDetailsPanel } from './GroupDetailsPanel'
import type { SharedInertiaProps } from '../../types/groupTypes'

const t = scopedTranslation('components.group_card')

export type GroupCardProps = {
  group: GroupsIndexQueryType['groups']['nodes'][0]
  dataCompId?: string
} & SharedInertiaProps

export default function GroupCard({ group, permissions, dataCompId }: GroupCardProps) {
  const { canEditGroup } = permissions
  const {
    id,
    name,
    description,
    streetAddress,
    city,
    postcode,
    state,
    pointPeople,
    notifyAllAdmins,
    publicUrl,
    status,
    emailContacts,
    phoneContacts,
    smsContacts,
    supporters,
    captains,
    criteria,
  } = group

  const groupUrl = `/groups/${id}`
  const totalContacts = emailContacts.totalCount + phoneContacts.totalCount + smsContacts.totalCount

  return (
    <div
      role="listitem"
      data-testid={`group-card-${name}`}
      {...(dataCompId && { 'data-compid': dataCompId })}
      className="tw:flex tw:flex-col tw:gap-5 tw:bg-white tw:w-full tw:p-4 tw:sm:p-6 tw:border tw:border-gray-200 tw:rounded-xl tw:shadow-xs"
    >
      <div className="tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-x-8 tw:gap-y-4">
        <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-3 tw:min-w-0 tw:max-w-2xl">
          <div className="tw:flex tw:flex-col tw:gap-1">
            <h2 className="tw:text-gray-900 tw:font-semibold tw:text-xl tw:w-fit tw:max-w-full tw:wrap-break-word tw:m-0">
              <InertiaLink
                href={groupUrl}
                className="tw:text-primary-700 tw:no-underline tw:border-b-2 tw:border-transparent tw:transition-colors tw:hover:text-primary-900 tw:hover:border-current tw:focus-visible:outline-none tw:focus-visible:rounded-sm tw:focus-visible:ring-4 tw:focus-visible:ring-primary-300"
              >
                {name}
              </InertiaLink>
            </h2>

            <GroupAddress streetAddress={streetAddress} city={city} postcode={postcode} state={state} />
          </div>

          {description && (
            <p
              data-testid="group-description"
              className="tw:m-0 tw:text-sm tw:text-gray-900 tw:whitespace-pre-wrap tw:line-clamp-2"
            >
              {description}
            </p>
          )}
        </div>

        <div className="tw:flex tw:flex-col tw:sm:flex-row tw:flex-wrap tw:shrink-0 tw:gap-3 tw:w-full tw:sm:w-auto">
          <SimpleStatCard
            value={supporters.totalCount}
            label={t('stats.supporters', { count: supporters.totalCount })}
          />

          <SplitStatCard value={totalContacts} label={t('stats.contacts', { count: totalContacts })}>
            <BreakdownLine
              icon="mail-01"
              iconColor="tw:text-gray-true-500"
              label={t('group_contacts_stats.emails', { count: emailContacts.totalCount })}
            />
            <BreakdownLine
              icon="phone-call-01"
              iconColor="tw:text-gray-true-500"
              label={t('group_contacts_stats.calls', { count: phoneContacts.totalCount })}
            />
            <BreakdownLine
              icon="message-chat-square"
              iconColor="tw:text-gray-true-500"
              label={t('group_contacts_stats.sms', { count: smsContacts.totalCount })}
            />
          </SplitStatCard>
        </div>
      </div>

      <GroupDetailsPanel>
        <GroupCriteria criteria={criteria} />

        <LeaderNames leaderType="captains" leaders={captains.nodes} />

        <PointPeople
          notifyAllAdmins={Boolean(notifyAllAdmins)}
          pointPersonEmails={pointPeople.nodes.map((pp) => pp.email)}
        />
      </GroupDetailsPanel>

      <div className="tw:border-t tw:border-gray-200 tw:w-full" />

      <div className="tw:flex tw:flex-wrap tw:gap-3 tw:sm:justify-between">
        <Link
          dataCompId={`${dataCompId}-groups-info-card_go-to-group`}
          size="sm"
          rank="primary"
          trailingIcon="arrow-circle-broken-right"
          ariaLabel={t('aria_labels.switch_to_group', { name })}
          href={groupUrl}
          as="inertiaLink"
        >
          {t('buttons.switch_to_group')}
        </Link>

        <span className="tw:inline-flex tw:flex-wrap tw:gap-3">
          {canEditGroup && (
            <>
              <Link
                dataCompId={`${dataCompId}-groups-info-card_edit-group`}
                size="sm"
                rank="secondary"
                leadingIcon="pencil-02"
                ariaLabel={t('aria_labels.edit_group', { name })}
                href={`/groups/${id}/edit`}
                as="inertiaLink"
              >
                {t('buttons.edit_group')}
              </Link>
              <Link
                size="sm"
                rank="secondary"
                leadingIcon="funnel"
                ariaLabel={t('aria_labels.edit_group_criteria', { name })}
                href={`/groups/${id}/criteria/edit`}
                as="inertiaLink"
              >
                {t('buttons.edit_group_criteria')}
              </Link>
              <Link
                dataCompId={`${dataCompId}-groups-info-card_manage-captains`}
                size="sm"
                rank="secondary"
                leadingIcon="users-01"
                ariaLabel={t('aria_labels.manage_leaders', { name })}
                href={`/groups/${id}/leaders`}
                as="inertiaLink"
              >
                {t('buttons.manage_leaders')}
              </Link>
            </>
          )}

          <Link
            size="sm"
            rank="secondary"
            leadingIcon="line-chart-up"
            ariaLabel={t('aria_labels.analytics', { name })}
            href={`/en/supporter_groups/${id}/analytics/supporters?v2=true`}
          >
            {t('buttons.analytics')}
          </Link>

          <Link
            size="sm"
            rank="secondary"
            leadingIcon="eye"
            ariaLabel={t('aria_labels.public_page', { name })}
            disabled={status === 'hidden' || !publicUrl}
            href={publicUrl ?? ''}
          >
            {t('buttons.public_page')}
          </Link>
        </span>
      </div>
    </div>
  )
}
